// Логиката на справката „Взаимозаменени продукти": плоските редове (една
// замяна = един ред; обикновено документът има по един), филтрите в колоните
// (Тип · Склад · Потребител · Донор · Получател), търсенето, сортирането и
// сумите. Бекендът връща най-новите отгоре — без избрано сортиране подредбата
// остава неговата.
import type { Product, Substitution } from "../../../../api/inventory";
import { clampDecimals, formatQty } from "../../components/qty";
import type { FilterSpecs } from "../../components/filters/columnFilterModel";
import {
  compareNumber,
  compareText,
  matchesWords,
  searchWords,
  sortRows,
  type ReportSort,
} from "./reportListModel";

export type SubstitutionRow = {
  key: string;
  date: string;
  source: string;
  warehouseName: string | null;
  createdByName: string | null;
  donorName: string;
  donorUnit: string | null;
  donorDecimals: number; // знаци за единицата на донора
  recipientName: string;
  recipientUnit: string | null;
  recipientDecimals: number; // знаци за единицата на получателя
  donorQty: number;
  recipientQty: number;
  unitPrice: number;
  sumNoVat: number;
  sum: number; // бруто
};

export const buildSubstitutionRows = (
  movements: Substitution[],
  products: Product[],
  vatRegistered: boolean,
): SubstitutionRow[] => {
  const vatById = new Map(products.map((product) => [product.id, product.vatRate ?? 0]));
  const decimalsById = new Map(products.map((product) => [product.id, clampDecimals(product.decimalPlaces)]));
  return movements.flatMap((movement) =>
    movement.lines.map((line) => {
      // Себестойността на ДОНОРА е винаги нето → Нето = сумата; Бруто =
      // нето × (1+ставка) при регистрирана фирма, иначе бруто = нето.
      const rate = vatById.get(line.donorProductId) ?? 0;
      return {
        key: `${movement.id}-${line.id}`,
        date: movement.date,
        source: movement.source,
        warehouseName: movement.warehouseName,
        createdByName: movement.createdByName,
        donorName: line.donorProductName,
        donorUnit: line.donorUnit,
        donorDecimals: decimalsById.get(line.donorProductId) ?? 3,
        recipientName: line.recipientProductName,
        recipientUnit: line.recipientUnit,
        recipientDecimals: decimalsById.get(line.recipientProductId) ?? 3,
        donorQty: line.donorQuantity,
        recipientQty: line.recipientQuantity,
        unitPrice: line.unitPrice,
        sumNoVat: line.sum,
        sum: vatRegistered && rate ? line.sum * (1 + rate / 100) : line.sum,
      };
    }),
  );
};

export const warehouseOf = (row: SubstitutionRow) => row.warehouseName || "—";
export const userOf = (row: SubstitutionRow) => row.createdByName || "—";

// ─── Филтри в колоните, търсене, сортиране ───────────────────────────────
export type SubFilters = {
  types: string[]; // източник: stocktake | …
  warehouses: string[];
  users: string[];
  donors: string[];
  recipients: string[];
};
export type SubFilterKey = keyof SubFilters;
export const EMPTY_SUB_FILTERS: SubFilters = {
  types: [],
  warehouses: [],
  users: [],
  donors: [],
  recipients: [],
};

// Донорът и получателят са продукти — списъкът им е дълъг, с търсачка
export const substitutionFilterSpecs = ({
  titles,
  sourceLabel,
}: {
  titles: Record<SubFilterKey, string>;
  sourceLabel: (source: string) => string;
}): FilterSpecs<SubstitutionRow, SubFilters> => ({
  types: { kind: "list", title: titles.types, valuesOf: (row) => [row.source], labelOf: sourceLabel },
  warehouses: { kind: "list", title: titles.warehouses, valuesOf: (row) => [warehouseOf(row)] },
  users: { kind: "list", title: titles.users, valuesOf: (row) => [userOf(row)] },
  donors: { kind: "list", title: titles.donors, valuesOf: (row) => [row.donorName], searchable: true },
  recipients: { kind: "list", title: titles.recipients, valuesOf: (row) => [row.recipientName], searchable: true },
});

export type SubSortKey =
  | "date"
  | "user"
  | "donor"
  | "recipient"
  | "donorQty"
  | "recipientQty"
  | "unitPrice"
  | "net"
  | "gross";
export type SubSort = ReportSort<SubSortKey>;

export const subSortFirstDir = (key: SubSortKey) =>
  key === "user" || key === "donor" || key === "recipient" ? "asc" : "desc";

const compareSubstitutions = (a: SubstitutionRow, b: SubstitutionRow, key: SubSortKey) => {
  switch (key) {
    case "date":
      return a.date.localeCompare(b.date);
    case "user":
      return compareText(userOf(a), userOf(b));
    case "donor":
      return compareText(a.donorName, b.donorName);
    case "recipient":
      return compareText(a.recipientName, b.recipientName);
    case "donorQty":
      return a.donorQty - b.donorQty;
    case "recipientQty":
      return a.recipientQty - b.recipientQty;
    case "unitPrice":
      return compareNumber(a.unitPrice, b.unitPrice);
    case "net":
      return a.sumNoVat - b.sumNoVat;
    case "gross":
      return a.sum - b.sum;
  }
};

// Търсачката над таблицата — базата, върху която работят филтрите в колоните
export const searchSubstitutions = (
  rows: SubstitutionRow[],
  query: string,
  sourceLabel: (source: string) => string,
) => {
  const words = searchWords(query);
  if (!words.length) return rows;
  return rows.filter((row) =>
    matchesWords(
      [
        row.donorName,
        row.recipientName,
        sourceLabel(row.source),
        row.warehouseName,
        row.createdByName,
        formatQty(row.donorQty, row.donorDecimals),
        formatQty(row.recipientQty, row.recipientDecimals),
      ],
      words,
    ),
  );
};

export const sortSubstitutions = (rows: SubstitutionRow[], sort: SubSort | null) =>
  sortRows(rows, sort, compareSubstitutions);

export const substitutionTotals = (rows: SubstitutionRow[]) => ({
  count: rows.length,
  net: rows.reduce((sum, row) => sum + (row.sumNoVat || 0), 0),
  gross: rows.reduce((sum, row) => sum + (row.sum || 0), 0),
});
